import React from "react";
import gql from "graphql-tag";
import { getPaginatedNodes } from "@utils";
import { addsDirectFundraiserGroup } from "@services";
import { Box, Flex, Text } from "@atoms";
import { FundsPageEarningsFundraisersList } from "../FundsPageEarningsFundraisersList";
import { FundsPageEarningsGroupsListFragment } from "./__generated__/FundsPageEarningsGroupsListFragment";

// == Types ================================================================

interface IProps {
  campaign: FundsPageEarningsGroupsListFragment;
}

// == Constants ============================================================

FundsPageEarningsGroupsList.fragments = gql`
  fragment FundsPageEarningsGroupsListFragment on Campaign {
    id
    name
    privacyLevel
    viewerFundraiser @include(if: $hasCurrentUser) {
      id
      userId
      name
      group {
        id
        name
      }
    }
    directAmountRaised {
      dollarsAmount
      formatted
    }
    groups {
      id
      name
      amountRaised {
        dollarsAmount
        formatted
      }
      paginatedFundraisers(first: $first, after: $after) {
        edges {
          cursor
          node {
            id
            userId
            group {
              id
              name
            }
            ...FundsPageEarningsFundraisersListFundraisersFragment
          }
        }
        pageInfo {
          endCursor
          hasNextPage
          hasPreviousPage
          startCursor
        }
      }
    }
    noGroupFundraisers {
      id
      name
      amountRaised {
        dollarsAmount
        formatted
      }
      paginatedFundraisers(first: $first, after: $after) {
        edges {
          cursor
          node {
            id
            userId
            group {
              id
              name
            }
            ...FundsPageEarningsFundraisersListFundraisersFragment
          }
        }
        pageInfo {
          endCursor
          hasNextPage
          hasPreviousPage
          startCursor
        }
      }
    }
    ...FundsPageEarningsFundraisersListViewerFundraiserFragment
    ...addsDirectFundraiserGroupFragment
  }
  ${FundsPageEarningsFundraisersList.fragments.viewerFundraiser}
  ${FundsPageEarningsFundraisersList.fragments.fundraisers}
  ${addsDirectFundraiserGroup.fragment}
`;

// == Component ============================================================

export default function FundsPageEarningsGroupsList({ campaign }: IProps) {
  const { groups, viewerFundraiser, noGroupFundraisers, privacyLevel } = campaign;
  const directFundraiserGroup = addsDirectFundraiserGroup(campaign);
  const isMultipleSubGroups = groups.length > 0;
  const combinedGroups = [...groups, noGroupFundraisers, directFundraiserGroup];

  const renderSubGroupHeader = (name: string, formatted: string) => {
    return (
      <Flex alignItems="row" justifyContent="space-between" padding={3}>
        <Box>
          <Text fontSize={[1, 1, 2]}>{name}</Text>
        </Box>
        <Box>
          <Text fontSize={[1, 1, 2]}>{formatted || "$0.00"}</Text>
        </Box>
      </Flex>
    );
  };

  return (
    <Box width={1}>
      {combinedGroups.map((group) => {
        const {
          id,
          name,
          amountRaised: { formatted },
          paginatedFundraisers: { edges },
        } = group;
        const fundraisers = getPaginatedNodes(edges);

        return (
          <Box bg="lightBg" key={`${id} ${name}`}>
            {isMultipleSubGroups && renderSubGroupHeader(name, formatted)}
            <FundsPageEarningsFundraisersList
              fundraisers={fundraisers}
              privacyLevel={privacyLevel}
              viewerFundraiser={viewerFundraiser}
            />
          </Box>
        );
      })}
    </Box>
  );
}

// == Styles ===============================================================
